import { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Copy, RefreshCw, Terminal, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { copyToClipboard } from '@/lib/utils';

interface UserProfile {
  id: string;
  email: string;
  username: string;
  apiToken: string;
  diskQuota: number;
  diskUsed: number;
  role: string;
}

export function Profile() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const fetchProfile = async () => {
    try {
      const meData = await api.getCurrentUser();
      const userData = await api.getUser(meData.id);
      setProfile(userData as UserProfile);
    } catch (_error) {
      toast.error('Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleDownloadShareXConfig = async () => {
    try {
      await api.downloadShareXConfig(profile?.username || 'user');
      toast.success('ShareX config downloaded');
    } catch (_error) {
      toast.error('Failed to download ShareX config');
    }
  };

  const handleDownloadBashScript = async () => {
    try {
      await api.downloadBashScript();
      toast.success('Bash script downloaded');
    } catch (_error) {
      toast.error('Failed to download bash script');
    }
  };

  const handleDownloadPowerShellScript = async () => {
    try {
      await api.downloadPowerShellScript();
      toast.success('PowerShell script downloaded');
    } catch (_error) {
      toast.error('Failed to download PowerShell script');
    }
  };

  const handleCopyApiToken = () => {
    if (profile?.apiToken) {
      copyToClipboard(profile.apiToken);
      toast.success('API token copied to clipboard');
    }
  };

  const handleRegenerateToken = async () => {
    if (
      !confirm(
        'Are you sure you want to regenerate your API token? Your existing configurations will stop working.'
      )
    ) {
      return;
    }

    setIsRegenerating(true);
    try {
      const data = await api.regenerateApiToken(profile?.id || '');
      setProfile((prev) => (prev ? { ...prev, apiToken: data.apiToken } : null));
      toast.success('API token regenerated - update your clients!');
    } catch (_error) {
      toast.error('Failed to regenerate token');
    } finally {
      setIsRegenerating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-8">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Profile</h1>
            <p className="mt-2 text-muted-foreground">
              Manage your account and download uploader configurations
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Account Info */}
            <Card>
              <CardHeader>
                <CardTitle>Account Information</CardTitle>
                <CardDescription>Your account details and storage usage</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Username</p>
                  <p className="font-medium">{profile?.username}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{profile?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Role</p>
                  <p className="font-medium capitalize">{profile?.role}</p>
                </div>
                <div>
                  <p className="mb-2 text-sm text-muted-foreground">Storage Usage</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>{((profile?.diskUsed || 0) / 1024 / 1024).toFixed(2)} MB</span>
                      <span>{((profile?.diskQuota || 0) / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-muted">
                      <div
                        className="h-2 rounded-full bg-primary transition-all"
                        style={{
                          width: `${Math.min(100, ((profile?.diskUsed || 0) / (profile?.diskQuota || 1)) * 100)}%`,
                        }}
                      />
                    </div>
                    <p className="text-right text-xs text-muted-foreground">
                      {(((profile?.diskUsed || 0) / (profile?.diskQuota || 1)) * 100).toFixed(1)}%
                      used
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* API Token */}
            <Card>
              <CardHeader>
                <CardTitle>API Token</CardTitle>
                <CardDescription>Your personal API token for uploads</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="password"
                      value={profile?.apiToken || ''}
                      readOnly
                      className="h-10 flex-1 rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
                    />
                    <Button variant="outline" size="icon" onClick={handleCopyApiToken}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Use this token in the X-API-Token header for API uploads
                  </p>
                </div>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleRegenerateToken}
                  disabled={isRegenerating}
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${isRegenerating ? 'animate-spin' : ''}`} />
                  {isRegenerating ? 'Regenerating...' : 'Regenerate Token'}
                </Button>

                <div className="border-t pt-4">
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    ⚠️ Regenerating your token will invalidate all existing uploader configurations.
                    You'll need to re-download and re-import your configs.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Uploader Configs */}
          <Card>
            <CardHeader>
              <CardTitle>Uploader Configurations</CardTitle>
              <CardDescription>
                Download configuration files for various upload clients
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                {/* ShareX */}
                <div className="space-y-3 rounded-lg border p-4">
                  <div className="flex items-center gap-2">
                    <Monitor className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold">ShareX</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Windows screenshot and file uploader. Files up to 100MB.
                  </p>
                  <Button onClick={handleDownloadShareXConfig} variant="outline" className="w-full">
                    <Download className="mr-2 h-4 w-4" />
                    Download .sxcu
                  </Button>
                </div>

                {/* Bash Script */}
                <div className="space-y-3 rounded-lg border p-4">
                  <div className="flex items-center gap-2">
                    <Terminal className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold">Bash Script</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Linux/Mac command-line uploader with chunking support.
                  </p>
                  <Button onClick={handleDownloadBashScript} variant="outline" className="w-full">
                    <Download className="mr-2 h-4 w-4" />
                    Download .sh
                  </Button>
                </div>

                {/* PowerShell */}
                <div className="space-y-3 rounded-lg border p-4">
                  <div className="flex items-center gap-2">
                    <Terminal className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold">PowerShell</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Windows PowerShell uploader with chunking for large files.
                  </p>
                  <Button
                    onClick={handleDownloadPowerShellScript}
                    variant="outline"
                    className="w-full"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download .ps1
                  </Button>
                </div>
              </div>

              <div className="mt-6 rounded-lg bg-muted p-4">
                <h4 className="mb-2 text-sm font-medium">Upload Limits</h4>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>
                    • <strong>ShareX:</strong> 100MB max (Cloudflare limit)
                  </li>
                  <li>
                    • <strong>Bash/PowerShell:</strong> Unlimited with automatic chunking
                  </li>
                  <li>
                    • <strong>Web Browser:</strong> Unlimited with automatic chunking
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
